import { Box, Flex, Text } from '@chakra-ui/react'

import { Icon } from '../../../../components/Icon'
import { LiveArtwork } from '../../../../services/Event/types'
import { formatBRL, formatBidTime, formatBRLShort } from './format'

interface ICurrentBidCardProps {
  artwork: LiveArtwork | null
  auctionEnded: boolean
}

/**
 * Maior lance da obra em destaque + histórico ao vivo dos lances dela.
 *
 * Depois que o leilão encerra o mesmo card vira o resultado da obra: valor final e
 * arrematante, ou "Não vendida" quando ela fechou sem lance.
 */
export function CurrentBidCard({ artwork, auctionEnded }: ICurrentBidCardProps) {
  const bids = artwork?.recent_bids || []
  const hasBid = Boolean(artwork?.current_bid)
  const isUnsold = auctionEnded && !hasBid

  let title = 'MAIOR LANCE ATUAL:'
  if (auctionEnded) title = isUnsold ? 'OBRA NÃO VENDIDA' : 'ARREMATADA POR:'

  let amount = formatBRLShort(artwork?.current_bid)
  if (!hasBid) amount = auctionEnded ? '—' : formatBRLShort(artwork?.starting_bid)

  return (
    <Flex direction="column" minH={0} gap="1.5vh">
      <Box
        // Degradê horizontal do mockup: roxo à esquerda, magenta à direita.
        bgGradient="linear(to-r, #67028E 0%, magenta.500 100%)"
        borderRadius="xl"
        px="1.7vw"
        py="1.9vh"
        flexShrink={0}
      >
        <Text fontSize="clamp(11px, 1vw, 22px)" fontWeight="700" letterSpacing="0.06em" color="whiteAlpha.900">
          {title}
        </Text>

        <Text
          fontSize="clamp(26px, 2.9vw, 62px)"
          fontWeight="800"
          lineHeight="1.15"
          color="white"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {amount}
        </Text>

        {!hasBid && !auctionEnded && (
          <Text fontSize="clamp(10px, 0.85vw, 18px)" color="whiteAlpha.800">
            Lance inicial — nenhum lance recebido até agora
          </Text>
        )}

        {hasBid && (
          // Sem fundo próprio: um fio separa o valor do arrematante, como no mockup.
          <Flex align="center" gap="0.8vw" borderTop="1px solid" borderColor="whiteAlpha.400" pt="1vh" mt="1vh">
            <Flex
              align="center"
              justify="center"
              w="2.2vw"
              h="2.2vw"
              minW="30px"
              minH="30px"
              borderRadius="full"
              bg="magenta.500"
              color="white"
              flexShrink={0}
            >
              <Icon name="lni lni-user" fontSize="clamp(12px, 1.1vw, 24px)" />
            </Flex>

            <Box minW={0} flex="1">
              {/* Entrelinha curta: o rótulo e o nome formam um bloco só, sem inflar o card. */}
              <Text fontSize="clamp(12px, 1vw, 22px)" lineHeight="1.05" color="whiteAlpha.900">
                Arrematante:
              </Text>
              <Text
                fontSize="clamp(12px, 1.15vw, 26px)"
                lineHeight="1.15"
                fontWeight="700"
                color="white"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {artwork?.current_bid_participant_name || artwork?.current_bid_tab_code || 'Não identificado'}
              </Text>
            </Box>

            {bids[0]?.created_at && (
              <Flex align="center" gap={1.5} color="white" flexShrink={0}>
                <Icon name="lni lni-timer" fontSize="clamp(12px, 0.95vw, 20px)" />
                <Text fontSize="clamp(12px, 0.95vw, 20px)" fontWeight="600">
                  {formatBidTime(bids[0].created_at)}
                </Text>
              </Flex>
            )}
          </Flex>
        )}
      </Box>

      <Flex direction="column" minH={0} flex="1">
        <Text
          fontSize="clamp(11px, 0.95vw, 21px)"
          fontWeight="700"
          letterSpacing="0.06em"
          color="magenta.500"
          mb="0.6vh"
          flexShrink={0}
        >
          HISTÓRICO AO VIVO:
        </Text>

        <Box
          flex="1"
          minH={0}
          overflowY="auto"
          pr={2}
          // O fade no rodapé deixa claro que a lista continua além do que cabe na tela.
          sx={{
            maskImage: 'linear-gradient(to bottom, black 82%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 82%, transparent 100%)',
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.25)', borderRadius: '4px' }
          }}
        >
          {bids.length === 0 ? (
            <Text fontSize="clamp(11px, 0.95vw, 21px)" color="whiteAlpha.600">
              Nenhum lance nesta obra ainda.
            </Text>
          ) : (
            bids.map((bid) => (
              <Text key={bid.id} fontSize="clamp(11px, 0.95vw, 21px)" color="whiteAlpha.900" mb="0.4vh">
                <Box as="span" color="whiteAlpha.600">
                  {formatBidTime(bid.created_at)} -{' '}
                </Box>
                {formatBRL(bid.bid_amount)}
                <Box as="span" color="whiteAlpha.600">
                  {' '}
                  - {bid.participant_name || bid.tab_code || 'Não identificado'}
                </Box>
              </Text>
            ))
          )}
        </Box>
      </Flex>
    </Flex>
  )
}
